<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">RFQ</h5>

    <section class="filter overflow-auto q-mb-md bg-gray">
      <div class="row q-col-gutter-md items-center q-pa-sm">
        <q-input
          v-model="search"
          dense
          class="col-xs-12 col-md-3"
          label="Search"
          hint="Search by shipper ID/email/name, container ID or delivery address"
          @keydown.enter="debouncedSendRequest"
        />

        <q-select
          v-model="city"
          emit-value
          map-options
          dense
          class="col-xs-12 col-sm-6 col-md-3"
          label="City"
          option-value="id"
          option-label="name"
          hint=""
          :options="cityList"
        />

        <q-select
          v-model="status"
          dense
          class="col-xs-12 col-sm-6 col-md-3"
          label="Status"
          hint=""
          emit-value
          map-options
          :options="statusOptions"
        />

        <div class="col-xs-12 col-md-3">
          <q-btn
            color="primary"
            icon="search"
            label="Search"
            :class="$q.screen.lt.md ? 'full-width' : ''"
            @click="debouncedSendRequest"
          />
        </div>
      </div>
    </section>

    <DocExport ref="docExport" @reset-pressed="onReset" @doc-search="onDocSearch" />

    <DataTable ref="table" wrap-cells :columns="columns">
      <template v-if="loading">
        <tr v-for="n in 10" :key="n">
          <td v-for="m in columns.length" :key="m">
            <q-skeleton type="text" />
          </td>
        </tr>
      </template>

      <tr v-else-if="!docs.length">
        <td class="text-center" :colspan="columns.length">No records found.</td>
      </tr>

      <tr v-for="quote in docs || 10" v-else :key="quote.id">
        <!-- Document id -->
        <td>
          <span class="text-more-info" @click="openRfqPdf(quote)">{{ quote.id | formatId }}</span>
        </td>

        <!-- Shipper email, ID -->
        <td>
          <span class="text-more-info" @click="openShipperDialog(quote.shipper)"
            >{{ quote.shipper.email }} <br />
            {{ quote.shipper.id | formatId }}</span
          >
        </td>

        <!-- Shipper name, company -->
        <td>
          {{ quote.shipper.name | capitalize }} <br />
          {{ quote.shipper.businessName | uppercase }}
        </td>

        <!-- Deliver from - to -->
        <td>
          <p class="q-mb-xs">
            <strong class="q-mr-sm">FR:</strong>{{ getDeliverAddress(quote, quote.type === 'import', true) }}
          </p>

          <p class="q-mb-xs">
            <strong class="q-mr-sm">TO:</strong>{{ getDeliverAddress(quote, quote.type !== 'import', true) }}
          </p>
        </td>

        <!-- Request status -->
        <td
          class="text-weight-medium"
          :class="getStatusClass(getStatus(quote.acceptedResponse, !!quote.responses.length))"
        >
          {{ getStatus(quote.acceptedResponse, !!quote.responses.length) }}
        </td>

        <!-- Waiting time -->
        <td>{{ getWaitingTime(quote) }}</td>

        <!-- Containers -->
        <td>
          <strong>Total {{ quote.containers.length }}:</strong>

          <div v-for="container in quote.containers.slice(0, 5)" :key="container.id" class="q-mt-xs">
            <span>{{ container.containerCode }}</span>
            <br />
            <span class="text-muted">{{ container.type | formatSlugToName }}</span>
          </div>

          <template v-if="quote.containers.length > 5">
            <br />
            <span>....</span>
          </template>
        </td>

        <!-- Delivery type -->
        <td style="min-width: 8rem">
          <span>
            {{ quote.truckType | formatSlugToName }}
            <q-tooltip anchor="top middle" self="center middle">Truck type</q-tooltip>
          </span>

          <br />

          <span>
            {{ quote.deliveryType[0] | formatSlugToName }}
            <q-tooltip anchor="top middle" self="center middle">Delivery type</q-tooltip>
          </span>

          <br />

          <span>
            {{ quote.doorsPosition[0] | formatSlugToName }}
            <q-tooltip anchor="top middle" self="center middle">Door position</q-tooltip>
          </span>
        </td>

        <!-- Number of quotes -->
        <td class="text-center">
          <span class="text-more-info" @click="openQuoteList(quote)">{{ quote.responses.length }}</span>
        </td>

        <!-- Average price -->
        <td>{{ getAveragePrice(quote) | formatCurrency }}</td>

        <!-- Vessel/Voyage -->
        <td style="min-width: 8rem">{{ quote.vessel | formatVesVoy }} / {{ quote.voyage | formatVesVoy }}</td>

        <!-- City -->
        <td>{{ quote.departureCity.name }}</td>

        <!-- Created time -->
        <td>{{ quote.createdAt | formatDateTime }}</td>
      </tr>
    </DataTable>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <template v-if="selectedRfq">
      <!-- If viewing responses then do not remove selected rfq after closing  -->
      <RFQDetailDialog
        :id="selectedRfq.id"
        :open.sync="showPdf"
        :response-id="selectedResponseId"
        @hide="showQuotes ? (showPdf = false) : (selectedRfq = null)"
      />

      <RFQQuotesDialog
        :response-id.sync="selectedResponseId"
        :open.sync="showQuotes"
        :request.sync="selectedRfq"
        @select="showPdf = true"
      />
    </template>

    <RFQUserDialog v-if="selectedShipper" :open.sync="showShipper" :user.sync="selectedShipper" />
  </Wrapper>
</template>

<script>
import en from 'date-fns/locale/en';
import distanceInWordsToNow from 'date-fns/distance_in_words_to_now';
import debounce from 'lodash/debounce';
import API from '@/apis';
import Pagination from '@/components/Pagination.vue';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTableNew.vue';
import { getStatus, getStatusClass, getDeliverAddress, getErrorMessage } from '@/utils';
import formatDateTime from '@/utils/mixins/formatDateMixin';
import DocExport from '../../components/DocExport.vue';

export default {
  name: 'AdminRFQ',

  components: {
    Wrapper,
    DocExport,
    Pagination,
    DataTable,
    RFQDetailDialog: () => import('./RFQDialog/RFQDetailDialog.vue'),
    RFQUserDialog: () => import('./RFQDialog/RFQUserDialog.vue'),
    RFQQuotesDialog: () => import('./RFQDialog/RFQQuotesDialog.vue'),
  },

  mixins: [formatDateTime],

  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      city: 'All',
      search: '',
      status: 'All',
      docs: [],
      id: '',
      cityList: [],
      loading: true,
      error: null,
      // Viewing request pdf
      selectedRfq: null,
      showPdf: false,
      // Viewing shipper
      showShipper: false,
      selectedShipper: null,
      // Viewing quotes
      showQuotes: false,
      selectedResponseId: '',
      statusOptions: [
        { label: 'All', value: 'all' },
        { label: 'Waiting', value: 'waiting' },
        { label: 'Pending', value: 'pending' },
        { label: 'Response Accepted', value: 'responded' },
      ],
      columns: [
        'Doc ID',
        'Shipper Email - ID',
        'Shipper Name',
        'Deliver From - To',
        'RFQ Status',
        'Waiting Time',
        'Containers',
        'Delivery Type',
        'No. of Quotes',
        'Average Price',
        'Vessel Voyage',
        'City',
        'Created Time',
      ],
    };
  },

  watch: {
    currentPage() {
      this.debouncedSendRequest();

      window.scrollTo({
        top: this.$refs.table.$el.offsetTop,
        left: 0,
        behavior: 'smooth',
      });
    },
  },

  async created() {
    this.debouncedSendRequest = debounce(this.sendRequest, 300);
    await this.getCityList();
    await this.sendRequest();
  },

  methods: {
    getStatus,
    getStatusClass,
    getDeliverAddress,
    onReset() {
      const changed =
        this.city !== 'All' || this.search !== '' || this.status !== 'All' || this.$refs.docExport.docId !== '';

      this.city = 'All';
      this.search = '';
      this.status = 'All';
      this.$refs.docExport.resetDocId();

      this.$nextTick(() => {
        if (changed) {
          this.sendRequest();
        }
      });
    },
    async onDocSearch(docId) {
      if (this.id === docId) return;

      this.id = docId;

      await this.sendRequest();

      this.id = '';
    },
    getParams() {
      const params = {};

      if (this.city !== 'All') {
        params.city = this.city;
      }

      if (this.search) {
        params.search = this.search;
      }

      if (this.id) {
        params.id = this.id;
      }

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      if (this.status !== 'All') {
        params.status = this.status;
      }

      return params;
    },
    getWaitingTime(doc) {
      return distanceInWordsToNow(doc.updatedAt, { locale: en });
    },
    getAveragePrice(doc) {
      if (!doc.responses.length) return 0;

      const total = doc.responses.reduce((acc, current) => acc + current.totalAud, 0);

      return total / doc.responses.length;
    },
    async getCityList() {
      const { data } = await this.$axios.request(API.city.getAll());

      this.cityList = [{ name: 'All', id: 'All' }, ...data.data];
    },
    async sendRequest() {
      this.loading = true;

      try {
        const { data } = await this.$axios.request(API.admin.getRFQ(this.getParams()));

        this.docs = data.data;
        this.totalPages = data.pagination.pages;
        this.currentPage = data.pagination.page;
      } catch (error) {
        this.$q.notify({
          type: 'negative',
          message: getErrorMessage(error),
          actions: [
            {
              label: 'Try again',
              color: 'yellow',
              handler: this.sendRequest,
            },
          ],
        });
      } finally {
        this.loading = false;
      }
    },
    debouncedSendRequest() {},
    openRfqPdf(rfq) {
      this.showPdf = true;
      this.selectedRfq = rfq;
    },
    openShipperDialog(shipper) {
      this.showShipper = true;
      this.selectedShipper = shipper;
    },
    openQuoteList(rfq) {
      this.showQuotes = true;
      this.selectedRfq = rfq;
    },
    handleCloseDialog() {
      this.showPdf = false;
      this.showShipper = false;
      this.showQuotes = false;
      this.selectedRfq = {};
    },
  },
};
</script>

<style lang="scss" scoped>
.text-more-info {
  color: $main;
  cursor: pointer;
  text-decoration: underline;
}
</style>
